// src/features/countries/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import {
  continentOptions,
  currencyOptions,
  economicalZoneOptions,
  emuOecdZoneOptions,
  marketGroupOptions,
  marketTypeOptions,
} from "./filter";

const client = "instit-quantilia";
const table_name = "countries";

// Transform enum-key options to numeric-string values for form submission
const econZoneOptions = economicalZoneOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));
const contOptions = continentOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));
const mGroupOptions = marketGroupOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));
const mTypeOptions = marketTypeOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));
// emuOecdZoneOptions already uses numeric strings
// currencyOptions are ISO code strings

// Schema definition
const FormSchema = z.object({
  code: z.string().min(1, "Code is required"),
  en_translation: z.string().nullable().optional(),
  fr_translation: z.string().nullable().optional(),
  economical_zone: z.coerce.number({
    message: "Economical Zone is required",
  }),
  market_group: z.coerce.number().optional().nullable(),
  market_type: z.coerce.number({
    message: "Market Type is required",
  }),
  continent: z.coerce.number({ message: "Continent is required" }),
  currency: z.string().nullable().optional(),
  emu_oecd_zone: z.coerce.number().optional().nullable(),
});

export function CountriesNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Country
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Code & English Translation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="en_translation"
                    label={t(`${table_name}.columns.en_translation`, {
                      ns: "tables",
                      defaultValue: "English Translation",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2: French Translation & Economical Zone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="fr_translation"
                    label={t(`${table_name}.columns.fr_translation`, {
                      ns: "tables",
                      defaultValue: "French Translation",
                    })}
                    control={form.control}
                  />
                  <SelectField
                    name="economical_zone"
                    label={t(`${table_name}.columns.economical_zone`, {
                      ns: "tables",
                      defaultValue: "Economical Zone",
                    })}
                    options={econZoneOptions}
                    control={form.control}
                  />
                </div>

                {/* Row 3: Market Group & Market Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="market_group"
                    label={t(`${table_name}.columns.market_group`, {
                      ns: "tables",
                      defaultValue: "Market Group",
                    })}
                    options={mGroupOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="market_type"
                    label={t(`${table_name}.columns.market_type`, {
                      ns: "tables",
                      defaultValue: "Market Type",
                    })}
                    options={mTypeOptions}
                    control={form.control}
                  />
                </div>

                {/* Row 4: Continent & Currency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="continent"
                    label={t(`${table_name}.columns.continent`, {
                      ns: "tables",
                      defaultValue: "Continent",
                    })}
                    options={contOptions}
                    control={form.control}
                  />
                  <SelectField
                    name="currency"
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 5: EMU OECD Zone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="emu_oecd_zone"
                    label={t(`${table_name}.columns.emu_oecd_zone`, {
                      ns: "tables",
                      defaultValue: "EMU OECD Zone",
                    })}
                    options={emuOecdZoneOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
